/**
 * Behaviour for the landing page's <details> menus (the phone menu and the
 * "More from PlayPuzzle" dropdown).
 *
 * <details> already opens, closes and announces its expanded state with no
 * script at all, so the menus work before this runs and without it. This adds
 * what a menu is expected to do on top: close when a link inside is followed
 * (an in-page #anchor would otherwise leave it open over the section), on a tap
 * outside, and on Escape — returning focus to the toggle — and keep only one
 * open at a time.
 *
 * Plain DOM so the prerendered page can run it without React; the SPA's
 * LandingPage calls the same function from an effect.
 */
export function wireMenus(root: ParentNode = document): () => void {
  const menus = () => Array.from(root.querySelectorAll<HTMLDetailsElement>('details[data-menu]'));

  function closeAll(except?: HTMLDetailsElement) {
    for (const menu of menus()) if (menu !== except) menu.open = false;
  }

  function onClick(event: Event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const menu = target.closest<HTMLDetailsElement>('details[data-menu]');
    if (!menu) {
      closeAll();
      return;
    }
    if (target.closest('a')) menu.open = false;
    // A click on the summary toggles this menu; any other one closes.
    else if (target.closest('summary')) closeAll(menu);
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    const open = menus().find((menu) => menu.open);
    if (!open) return;
    open.open = false;
    open.querySelector('summary')?.focus();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  return () => {
    document.removeEventListener('click', onClick);
    document.removeEventListener('keydown', onKeydown);
  };
}
